import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/shared/Logo';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import { PinInput } from '../components/auth/PinInput';
import api from '../services/api';
import './SendMoneyScreen.css';

interface RecipientInfo {
  name: string;
  walletId: string;
  phone: string;
}

export const SendMoneyScreen: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [step, setStep] = useState<'scan' | 'manual' | 'amount' | 'confirm' | 'success'>('scan');
  const [recipient, setRecipient] = useState<RecipientInfo | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualWalletId, setManualWalletId] = useState('');
  const [mainWallet, setMainWallet] = useState<any>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [pin, setPin] = useState('');
  const [pinVerified, setPinVerified] = useState(false);

  useEffect(() => {
    const fetchMainWallet = async () => {
      try {
        setWalletLoading(true);
        const wallets = await api.wallets.getWallets();
        const wallet = wallets.find(w => w.type === 'Main');
        setMainWallet(wallet);
      } catch (err) {
        console.error('Failed to fetch wallets:', err);
        setError('Failed to load wallet information');
      } finally {
        setWalletLoading(false);
      }
    };

    fetchMainWallet();
  }, []);

  useEffect(() => {
    if (step === 'scan') {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [step]);

  const handleManualInput = async () => {
    if (!manualWalletId.trim()) {
      setError('Please enter a wallet ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate wallet ID via API
      const recipientData = await api.wallets.validateWalletId(manualWalletId.trim());
      
      // Create recipient info from API response
      const recipient: RecipientInfo = {
        name: recipientData.name,
        walletId: recipientData.walletId,
        phone: recipientData.phone
      };
      
      setRecipient(recipient);
      setStep('amount');
    } catch (err: any) {
      console.error('Wallet ID validation failed:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 404) {
        setError('Wallet ID not found. Please check the wallet ID and try again.');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Invalid wallet ID format. Expected format: YW-XX-123456');
      } else {
        setError('Failed to validate wallet ID. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError('');
      
      // Check if camera is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      // First check permissions
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        console.log('Camera permission status:', permissionStatus.state);
      } catch (permError) {
        console.log('Permission query not supported, proceeding with getUserMedia');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraActive(true);
      }
    } catch (err: any) {
      console.error('Camera access error:', err);
      let errorMessage = 'Camera access failed.';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please:\n1. Refresh the page\n2. Allow camera access when prompted\n3. Check browser settings if needed';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Camera constraints not supported. Trying fallback...';
        // Try with simpler constraints
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true
          });
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            setIsCameraActive(true);
            return;
          }
        } catch (fallbackErr) {
          errorMessage = 'Camera access failed with fallback constraints.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setCameraError(errorMessage);
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const simulateQRScan = () => {
    // Simulate QR code scanning result
    setLoading(true);
    setTimeout(() => {
      const mockRecipient: RecipientInfo = {
        name: 'John Doe',
        walletId: 'YW-JD-789012',
        phone: '+27 83 987 6543'
      };
      setRecipient(mockRecipient);
      setStep('amount');
      setLoading(false);
    }, 2000);
  };

  const handleManualEntry = () => {
    setStep('manual');
    // Clear any existing data
    setManualWalletId('');
    setRecipient(null);
    setError('');
  };

  const validateAmount = () => {
    if (!mainWallet) {
      setError('Main wallet not available');
      return false;
    }
    
    const amountNum = parseFloat(amount);
    if (!amount || amountNum <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    if (amountNum > mainWallet.availableBalance) {
      setError(`Insufficient funds. Available balance: R${mainWallet.availableBalance.toFixed(2)}`);
      return false;
    }
    if (amountNum < 1) {
      setError('Minimum transfer amount is R1.00');
      return false;
    }
    return true;
  };

  const handleContinueToConfirm = () => {
    if (validateAmount()) {
      setStep('confirm');
      // Reset PIN verification when entering confirm step
      setPinVerified(false);
      setPin('');
    }
  };

  const handleVerifyPin = async () => {
    if (!pin || pin.length !== 4) {
      setError('Please enter your 4-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.auth.verifyPin(pin);
      setPinVerified(true);
      setError('');
    } catch (err: any) {
      console.error('PIN verification failed:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Invalid PIN. Please try again.');
      }
      setPin(''); // Clear PIN on error
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!recipient) {
      setError('Recipient information is missing');
      return;
    }

    if (!pinVerified) {
      setError('Please verify your PIN first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (!mainWallet) {
        throw new Error('Main wallet not available');
      }

      // Make real transfer API call
      const transferResult = await api.wallets.transferMoney({
        fromWalletId: mainWallet.id,
        toWalletId: recipient.walletId,
        amount: parseFloat(amount),
        reference: reference || undefined,
      });

      console.log('Transfer successful:', transferResult);
      setStep('success');
    } catch (err: any) {
      console.error('Transfer failed:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Invalid transfer request');
      } else if (err.response?.status === 404) {
        setError('Wallet not found');
      } else {
        setError('Transfer failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toFixed(2)}`;
  };

  // Show loading state while fetching wallet information
  if (walletLoading) {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Send Money</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="loading-state">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Loading wallet information...</p>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  // Show error if Main wallet is not available
  if (!mainWallet) {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Send Money</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="error-state">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Main Wallet Required</h3>
              <p>You need a Main wallet to send money. Please contact support if you don't see your Main wallet.</p>
              <button onClick={() => navigate('/dashboard')} className="secondary-button">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (step === 'manual') {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => setStep('scan')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Enter Wallet ID</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>

          <div className="screen-content">
            <div className="manual-input-section">
              <h3>Enter Recipient's Wallet ID</h3>
              <p>Enter the wallet ID of the person you want to send money to</p>
              
              <div className="input-container">
                <label htmlFor="walletId">Wallet ID</label>
                <input
                  id="walletId"
                  type="text"
                  value={manualWalletId}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase();
                    
                    // Remove all non-alphanumeric characters except hyphens
                    value = value.replace(/[^A-Z0-9-]/g, '');
                    
                    // Remove existing hyphens to rebuild the format
                    const cleanValue = value.replace(/-/g, '');
                    
                    // Apply the YW-XXX-12345 format (where XXX includes type suffix)
                    let formatted = '';
                    for (let i = 0; i < cleanValue.length && i < 10; i++) {
                      if (i === 2 || i === 5) {
                        formatted += '-';
                      }
                      formatted += cleanValue[i];
                    }
                    
                    setManualWalletId(formatted);
                  }}
                  placeholder="YW-ABM-12345"
                  className="wallet-id-input"
                  disabled={loading}
                  maxLength={12}
                />
                <div className="format-hint">
                  <i className="fas fa-info-circle"></i>
                  Format: YW-XXX-12345 (M=Main, G=Grant, R=Rewards)
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="manual-actions">
                <button 
                  onClick={handleManualInput}
                  disabled={loading || !manualWalletId.trim()}
                  className="primary-button"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Validating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Continue
                    </>
                  )}
                </button>
                
                <button 
                  onClick={() => setStep('scan')}
                  className="secondary-button"
                  disabled={loading}
                >
                  <i className="fas fa-qrcode"></i>
                  Scan QR Code Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <div></div>
            <h1>Transfer Complete!</h1>
          </div>
          
          <div className="screen-content">
            <div className="success-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>Money Sent Successfully!</h2>
              <div className="transfer-summary">
                <div className="summary-item">
                  <span className="label">Amount Sent:</span>
                  <span className="value">{formatCurrency(parseFloat(amount))}</span>
                </div>
                <div className="summary-item">
                  <span className="label">To:</span>
                  <span className="value">{recipient?.name}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Wallet ID:</span>
                  <span className="value">{recipient?.walletId}</span>
                </div>
                {reference && (
                  <div className="summary-item">
                    <span className="label">Reference:</span>
                    <span className="value">{reference}</span>
                  </div>
                )}
              </div>
              <div className="success-actions">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="primary-button"
                >
                  Back to Dashboard
                </button>
                <button 
                  onClick={() => {
                    setStep('scan');
                    setRecipient(null);
                    setAmount('');
                    setReference('');
                    setError('');
                    setPinVerified(false);
                    setPin('');
                  }}
                  className="secondary-button"
                >
                  Send Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <button onClick={() => setStep('amount')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Confirm Transfer</h1>
          </div>

          <div className="screen-content">
            <div className="confirmation-details">
              <div className="recipient-card">
                <div className="recipient-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <div className="recipient-info">
                  <h3>{recipient?.name}</h3>
                  <p>{recipient?.walletId}</p>
                </div>
              </div>

              <div className="transfer-details">
                <div className="amount-display">
                  <span className="currency">R</span>
                  <span className="amount">{parseFloat(amount).toFixed(2)}</span>
                </div>
                
                <div className="transfer-info">
                  <div className="info-row">
                    <span className="label">From:</span>
                    <span className="value">Main Wallet</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Available Balance:</span>
                    <span className="value">{mainWallet ? formatCurrency(mainWallet.availableBalance) : 'Loading...'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">New Balance:</span>
                    <span className="value">{mainWallet ? formatCurrency(mainWallet.availableBalance - parseFloat(amount)) : 'Loading...'}</span>
                  </div>
                  {reference && (
                    <div className="info-row">
                      <span className="label">Reference:</span>
                      <span className="value">{reference}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* PIN Verification Section */}
            {!pinVerified ? (
              <div className="pin-verification-section">
                <h3>Enter your PIN to confirm</h3>
                <p>Please enter your 4-digit PIN to complete this transfer</p>
                
                <div className="pin-input-container">
                  <PinInput 
                    length={4}
                    onComplete={(pin) => setPin(pin)}
                    error={!!error}
                    disabled={loading}
                  />
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="pin-actions">
                  <button 
                    onClick={handleVerifyPin}
                    disabled={loading || pin.length !== 4}
                    className="primary-button"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-shield-alt"></i>
                        Verify PIN
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => setStep('amount')}
                    disabled={loading}
                    className="secondary-button"
                  >
                    Back
                  </button>
                </div>
              </div>
            ) : (
              <div className="pin-verified-section">
                <div className="verification-success">
                  <i className="fas fa-check-circle"></i>
                  <span>PIN Verified Successfully</span>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="confirmation-actions">
                  <button 
                    onClick={handleConfirmTransfer}
                    disabled={loading}
                    className="primary-button"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Processing Transfer...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Complete Transfer
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => {
                      setPinVerified(false);
                      setPin('');
                      setError('');
                    }}
                    disabled={loading}
                    className="secondary-button"
                  >
                    Change PIN
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'amount') {
    return (
      <div className="send-money-screen">
        <div className="send-money-container">
          <div className="screen-header">
            <button onClick={() => setStep('scan')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Send Amount</h1>
          </div>

          <div className="screen-content">
            {recipient && (
              <div className="recipient-card">
                <div className="recipient-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <div className="recipient-info">
                  <h3>{recipient.name}</h3>
                  <p>{recipient.walletId}</p>
                </div>
                <div className="verified-badge">
                  <i className="fas fa-check-circle"></i>
                  Verified
                </div>
              </div>
            )}

            <div className="amount-section">
              <label htmlFor="amount">Amount to Send</label>
              <div className="amount-input-container">
                <span className="currency-symbol">R</span>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError('');
                  }}
                  className="amount-input"
                />
              </div>
              <div className="balance-info">
                Available: {mainWallet ? formatCurrency(mainWallet.availableBalance) : 'Loading...'}
                <br />
                <small style={{ opacity: 0.7 }}>Transfers are only allowed from Main wallet</small>
              </div>
            </div>

            <div className="reference-section">
              <label htmlFor="reference">Reference (Optional)</label>
              <input
                id="reference"
                type="text"
                placeholder="Payment reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="reference-input"
                maxLength={50}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button 
                onClick={handleContinueToConfirm}
                className="primary-button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="send-money-screen">
      <div className="send-money-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Send Money</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          <div className="scan-section">
            <h3>Scan Recipient's QR Code</h3>
            
            <div className="camera-container">
              {cameraError ? (
                <div className="camera-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  <div className="error-text">
                    {cameraError.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                  <div className="error-actions">
                    <button onClick={startCamera} className="retry-button">
                      <i className="fas fa-redo"></i>
                      Request Camera Access
                    </button>
                    <button 
                      onClick={() => setStep('manual')}
                      className="skip-button"
                    >
                      <i className="fas fa-keyboard"></i>
                      Enter Manually
                    </button>
                  </div>
                </div>
              ) : loading ? (
                <div className="camera-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Scanning QR Code...</p>
                </div>
              ) : (
                <div className="camera-view">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-video"
                  />
                  <div className="scan-overlay">
                    <div className="scan-frame">
                      <div className="scan-corner tl"></div>
                      <div className="scan-corner tr"></div>
                      <div className="scan-corner bl"></div>
                      <div className="scan-corner br"></div>
                    </div>
                    <p className="scan-instruction">
                      Position the QR code within the frame
                    </p>
                  </div>
                </div>
              )}
            </div>

            {isCameraActive && (
              <button onClick={simulateQRScan} className="simulate-scan-button">
                Simulate QR Scan (Demo)
              </button>
            )}
          </div>

          <div className="alternative-options">
            <div className="divider">
              <span>or</span>
            </div>
            
            <button onClick={handleManualEntry} className="manual-entry-button">
              <i className="fas fa-keyboard"></i>
              Enter Wallet ID Manually
            </button>
            
            <button onClick={() => navigate('/receive-money')} className="receive-money-button">
              <i className="fas fa-qrcode"></i>
              Show My QR Code
            </button>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};